import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import Header from "../../component/Header";
import Sidebar from "../../component/Sidebar";
import { toast } from "react-toastify";
import noImage from "../../images/errors.png";

const apiUrl = process.env.REACT_APP_API_URL;

const ScannerPage = () => {
  const [scannedCode, setScannedCode] = useState("");
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editData, setEditData] = useState({});
  const [cameraError, setCameraError] = useState("");
  const [scanning, setScanning] = useState(false);

  // Camera selection state
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState("");

  // Quick Scan Mode state
  const [quickScanMode, setQuickScanMode] = useState(false);
  const [quickScanStatus, setQuickScanStatus] = useState("Received");
  const [quickScanTable, setQuickScanTable] = useState("all");
  const [quickScanCarrier, setQuickScanCarrier] = useState("");
  const [quickScanLog, setQuickScanLog] = useState([]);
  const quickScanRef = useRef({ active: false, status: "Received", carrier: "", table: "products" });

  // Print states
  const [fromOrder, setFromOrder] = useState("");
  const [toOrder, setToOrder] = useState("");
  const [printLoading, setPrintLoading] = useState(false);

  const userId = localStorage.getItem("token");
  const userName = localStorage.getItem("user") ? localStorage.getItem("user").charAt(0).toUpperCase() + localStorage.getItem("user").slice(1) : "";
  const userRole = localStorage.getItem("role");
  const scannerRef = useRef(null);
  const isPausedRef = useRef(false);
  const selectedCameraRef = useRef("");
  const startScanningRef = useRef(null);

  // Keep refs in sync with state
  useEffect(() => {
    quickScanRef.current = { active: quickScanMode, status: quickScanStatus, carrier: quickScanCarrier, table: quickScanTable };
  }, [quickScanMode, quickScanStatus, quickScanCarrier, quickScanTable]);

  useEffect(() => {
    selectedCameraRef.current = selectedCamera;
  }, [selectedCamera]);

  useEffect(() => {
    // Request permission and get all available cameras
    Html5Qrcode.getCameras()
      .then((devices) => {
        if (devices && devices.length > 0) {
          setCameras(devices);
          console.log("📷 Available cameras:", devices.map((d, i) => `[${i}] id=${d.id} label="${d.label}"`));

          // Filter rear-facing cameras
          const rearCameras = devices.filter(d => {
            const label = d.label.toLowerCase();
            return label.includes("back") || label.includes("environment") || label.includes("rear");
          });

          // On Android, "camera2 0" is the main 1x camera
          // "camera2 2" or higher numbers are wide/ultra-wide/macro lenses
          let chosen = null;
          if (rearCameras.length > 1) {
            // Pick the rear camera with the lowest camera number (camera2 0 = main)
            chosen = rearCameras.find(d => d.label.includes("camera2 0"));
            // Fallback: pick the last rear camera listed
            if (!chosen) chosen = rearCameras[rearCameras.length - 1];
          } else if (rearCameras.length === 1) {
            chosen = rearCameras[0];
          }
          const finalCamera = chosen || devices[0];
          console.log("📷 Selected camera:", finalCamera.id, finalCamera.label);
          setSelectedCamera(finalCamera.id);
        }
      })
      .catch((err) => {
        console.error("Error getting cameras", err);
        setCameraError("Camera permission denied. Please allow camera access.");
      });

    const scanner = new Html5Qrcode("reader");
    scannerRef.current = scanner;

    return () => {
      try {
        scanner.stop();
      } catch (e) { }
    };
  }, []);

  const fetchProduct = useCallback(async (code) => {
    try {
      setLoading(true);
      let res;
      const searchUrl = quickScanTable === "all"
        ? `${apiUrl}/productByBarcode/${code}`
        : `${apiUrl}/productByBarcode/${code}?table=${quickScanTable}`;

      try {
        res = await axios.get(searchUrl);
      } catch (e) {
        const fallbackUrl = quickScanTable === "all"
          ? `${apiUrl}/productByOrderNumber/${encodeURIComponent(code)}`
          : `${apiUrl}/productByOrderNumber/${encodeURIComponent(code)}?table=${quickScanTable}`;
        res = await axios.get(fallbackUrl);
      }

      const data = res.data;
      setProduct(data);
      setEditData({
        soldPrice: data.soldPrice || "",
        salesDate: data.salesDate || "",
        seller: data.seller || userName || "",
        ourStat: data.ourStat || "",
        buyer: data.buyer || "",
        carrier: data.carrier || "",
      });
      isPausedRef.current = true; // Pause scanner to show results
    } catch (err) {
      setProduct(null);
      toast.error(`"${code}" not found. Try selecting the correct category if using Quick Scan.`);
      isPausedRef.current = false;
      setScannedCode("");
    } finally {
      setLoading(false);
    }
  }, [userName, quickScanTable]);

  // Quick scan: fetch product, update status, auto-resume scanning
  const quickScanUpdate = useCallback(async (code, status, carrier, table = "products") => {
    try {
      // 1. Fetch product (try barcode first, then order number)
      let res;
      const searchUrl = table === "all"
        ? `${apiUrl}/productByBarcode/${code}`
        : `${apiUrl}/productByBarcode/${code}?table=${table}`;

      try {
        res = await axios.get(searchUrl);
      } catch (e) {
        const fallbackUrl = table === "all"
          ? `${apiUrl}/productByOrderNumber/${encodeURIComponent(code)}`
          : `${apiUrl}/productByOrderNumber/${encodeURIComponent(code)}?table=${table}`;
        res = await axios.get(fallbackUrl);
      }
      const prod = res.data;
      const actualTable = prod.sourceTable || table;

      // 2. Update ourStat and carrier
      let updateUrl;
      let updateData;
      let isJson = false;

      if (actualTable === "products") {
        updateUrl = `${apiUrl}/updateItem/${prod.ID}`;
        const formData = new FormData();
        formData.append("ourStat", status);
        if (carrier !== undefined && carrier !== "") formData.append("carrier", carrier);
        updateData = formData;
      } else if (actualTable === "otherproducts") {
        updateUrl = `${apiUrl}/updateOtherItem/${prod.ID}`;
        const formData = new FormData();
        formData.append("status", status); // otherproducts uses 'status' not 'ourStat'
        if (carrier !== undefined && carrier !== "") formData.append("carrier", carrier);
        updateData = formData;
      } else if (actualTable === "screen") {
        updateUrl = `${apiUrl}/updateScreen/${prod.ID}`;
        const formData = new FormData();
        formData.append("status", status); // screen uses 'status'
        if (carrier !== undefined && carrier !== "") formData.append("carrier", carrier);
        updateData = formData;
      } else if (actualTable === "ethioproducts" || actualTable === "ethioProducts") {
        updateUrl = `${apiUrl}/updateEthioProducts`;
        updateData = { id: prod.ID, ourStat: status };
        isJson = true;
      } else if (actualTable === "ethioothers" || actualTable === "ethioOthers") {
        updateUrl = `${apiUrl}/updateOurStatEthioOther`;
        updateData = { id: prod.ID, ourStat: status };
        isJson = true;
      }

      if (isJson) {
        await axios.put(updateUrl, updateData, {
          headers: { Authorization: `Bearer ${userId}` },
        });
      } else {
        await axios.put(updateUrl, updateData, {
          headers: { Authorization: `Bearer ${userId}` },
        });
      }

      // 3. Log it
      setQuickScanLog(prev => [{
        order: prod.OrderNumber,
        brand: prod.Brand || "—",
        model: prod.Model || prod.ItemName || "—",
        status: status,
        carrier: carrier || "0",
        table: actualTable,
        time: new Date().toLocaleTimeString(),
      }, ...prev]);

      toast.success(`✅ ${prod.OrderNumber} → ${status}${carrier ? ` (Carrier: ${carrier})` : ""} [${actualTable}]`);
    } catch (err) {
      toast.error(`❌ "${code}" — failed to update`);
      setQuickScanLog(prev => [{
        order: code,
        brand: "—",
        model: "—",
        status: "FAILED",
        carrier: "—",
        table: table,
        time: new Date().toLocaleTimeString(),
      }, ...prev]);
    }

    // 4. Resume scanning after a short delay
    isPausedRef.current = false;
    setTimeout(() => {
      if (startScanningRef.current) startScanningRef.current();
    }, 800);
  }, [userName, userId]);

  const startScanning = useCallback(async () => {
    if (scannerRef.current) {
      try {
        // Stop any existing scan first before starting a new one
        try { await scannerRef.current.stop(); } catch (e) { }

        // Start scanning with the explicitly selected camera (use ref for freshest value)
        const cameraId = selectedCameraRef.current || selectedCamera;
        await scannerRef.current.start(
          cameraId || { facingMode: "environment" },
          {
            fps: 15,
            qrbox: { width: 300, height: 100 },
            experimentalFeatures: {
              useBarCodeDetectorIfSupported: true
            }
          },
          (decodedText, decodedResult) => {
            console.log("🔍 SCANNED RAW DATA:", decodedText, decodedResult);

            if (!isPausedRef.current) {
              // const validPattern = /^Mar\d{5}$/i;

              // if (!validPattern.test(decodedText)) {
              //   console.warn("⚠️ CODE REJECTED (Invalid Format):", decodedText);
              //   toast.error(`Scanned: ${decodedText}. Invalid format.`);
              //   return;
              // }

              console.log("✅ ACCEPTED CODE:", decodedText);
              isPausedRef.current = true;
              setScannedCode(decodedText);

              // Check if quick scan mode is active (using ref for fresh value)
              if (quickScanRef.current.active) {
                // Quick scan: auto-update and keep going
                scannerRef.current.stop().then(() => {
                  setScanning(false);
                  quickScanUpdate(decodedText, quickScanRef.current.status, quickScanRef.current.carrier, quickScanRef.current.table);
                }).catch(() => {
                  quickScanUpdate(decodedText, quickScanRef.current.status, quickScanRef.current.carrier, quickScanRef.current.table);
                });
              } else {
                // Normal mode: stop and show product details
                scannerRef.current.stop().then(() => {
                  setScanning(false);
                  fetchProduct(decodedText);
                }).catch(() => {
                  fetchProduct(decodedText);
                });
              }
            }
          },
          (errorMessage) => {
            if (!errorMessage.includes("No MultiFormat Readers were able to detect the code")) {
              console.debug("Frame Scan Error:", errorMessage);
            }
          }
        );
        setScanning(true);
        setCameraError("");
      } catch (err) {
        console.error("Camera start error:", err);
        setCameraError(`Camera could not start. Please make sure no other app is using it. Details: ${err}`);
      }
    }
  }, [selectedCamera, fetchProduct, quickScanUpdate]);

  // Keep startScanningRef in sync so quickScanUpdate always calls the latest version
  useEffect(() => {
    startScanningRef.current = startScanning;
  }, [startScanning]);

  const stopScanning = useCallback(async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
      } catch (e) { }
      setScanning(false);
    }
  }, []);

  const handleManualSearch = (e) => {
    e.preventDefault();
    if (scannedCode) {
      stopScanning();
      if (quickScanMode) {
        quickScanUpdate(scannedCode, quickScanStatus, quickScanCarrier, quickScanTable);
      } else {
        fetchProduct(scannedCode);
      }
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file && scannerRef.current) {
      try {
        await stopScanning();
        const result = await scannerRef.current.scanFile(file, true);

        // const validPattern = /^Mar\d{5}$/i;
        // if (!validPattern.test(result)) {
        //   toast.error("Invalid barcode format in picture. Must be CODE 128 (e.g., Mar26030).");
        //   return;
        // }

        setScannedCode(result);
        if (quickScanMode) {
          quickScanUpdate(result, quickScanStatus, quickScanCarrier, quickScanTable);
        } else {
          fetchProduct(result);
        }
      } catch (err) {
        toast.error("Could not read barcode from this image. Try finding better lighting or focus.");
      }
    }
  };

  const handleChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const handleSaveAll = async () => {
    try {
      setLoading(true);
      const table = product.sourceTable || "products";
      let updateUrl;
      let updateData;
      let isJson = false;

      if (table === "products") {
        updateUrl = `${apiUrl}/updateItem/${product.ID}`;
        const formData = new FormData();
        if (editData.ourStat !== undefined) formData.append("ourStat", editData.ourStat);
        if (editData.seller !== undefined) formData.append("seller", editData.seller);
        if (editData.buyer !== undefined) formData.append("buyer", editData.buyer);
        if (editData.soldPrice !== undefined) formData.append("soldPrice", editData.soldPrice);
        if (editData.salesDate !== undefined) formData.append("salesDate", editData.salesDate);
        if (editData.carrier !== undefined) formData.append("carrier", editData.carrier);
        updateData = formData;
      } else if (table === "otherproducts") {
        updateUrl = `${apiUrl}/updateOtherItem/${product.ID}`;
        const formData = new FormData();
        if (editData.ourStat !== undefined) formData.append("status", editData.ourStat); // otherproducts uses 'status'
        if (editData.seller !== undefined) formData.append("seller", editData.seller);
        if (editData.buyer !== undefined) formData.append("buyer", editData.buyer);
        if (editData.soldPrice !== undefined) formData.append("soldPrice", editData.soldPrice);
        if (editData.salesDate !== undefined) formData.append("salesDate", editData.salesDate);
        if (editData.carrier !== undefined) formData.append("carrier", editData.carrier);
        updateData = formData;
      } else if (table === "screen") {
        updateUrl = `${apiUrl}/updateScreen/${product.ID}`;
        const formData = new FormData();
        if (editData.ourStat !== undefined) formData.append("status", editData.ourStat); // screen uses 'status'
        if (editData.seller !== undefined) formData.append("seller", editData.seller);
        if (editData.buyer !== undefined) formData.append("buyer", editData.buyer);
        if (editData.soldPrice !== undefined) formData.append("soldPrice", editData.soldPrice);
        if (editData.salesDate !== undefined) formData.append("salesDate", editData.salesDate);
        if (editData.carrier !== undefined) formData.append("carrier", editData.carrier);
        updateData = formData;
      } else if (table === "ethioProducts") {
        updateUrl = `${apiUrl}/updateEthioProducts`;
        updateData = {
          id: product.ID,
          ourStat: editData.ourStat,
          soldPrice: editData.soldPrice,
          buyer: editData.buyer,
          seller: editData.seller,
          salesDate: editData.salesDate
        };
        isJson = true;
      } else if (table === "ethioOthers") {
        updateUrl = `${apiUrl}/updateOurStatEthioOther`;
        updateData = { id: product.ID, ourStat: editData.ourStat };
        isJson = true;
      }

      await axios.put(updateUrl, updateData, {
        headers: {
          Authorization: `Bearer ${userId}`,
          ...(isJson ? { "Content-Type": "application/json" } : {})
        },
      });

      toast.success(`Updated successfully in [${table}]!`);
      fetchProduct(scannedCode);
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Failed to update. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resumeScan = () => {
    setProduct(null);
    setScannedCode("");
    isPausedRef.current = false;
    startScanning();
  };

  const toggleQuickScanMode = () => {
    if (quickScanMode) {
      // Turning off: stop scanning, reset
      stopScanning();
      setQuickScanMode(false);
      setProduct(null);
      setScannedCode("");
      isPausedRef.current = false;
    } else {
      // Turning on: clear product view, enter quick mode
      setProduct(null);
      setScannedCode("");
      isPausedRef.current = false;
      setQuickScanMode(true);
    }
  };

  // --- Print Helpers (from Print.jsx) ---
  const parseOrder = (orderNum) => {
    const trimmed = orderNum.trim();
    const lastSpace = trimmed.lastIndexOf(" ");
    if (lastSpace === -1) return null;
    const prefix = trimmed.slice(0, lastSpace);
    const numStr = trimmed.slice(lastSpace + 1);
    const num = parseInt(numStr, 10);
    if (!prefix || isNaN(num)) return null;
    return { prefix, num, padLen: numStr.length };
  };

  const buildOrderNumber = (prefix, num, padLen) => {
    return `${prefix} ${String(num).padStart(padLen, "0")}`;
  };

  const printAllLabels = (products) => {
    const win = window.open("", "", "width=400,height=600");
    if (!win) {
      toast.error("Popup blocked! Please allow popups for this site and try again.");
      return;
    }

    const labelsHtml = products
      .map((product, i) => {
        const title = [product.Brand, product.Model].filter(Boolean).join(" ");
        let specStr = "";
        if (product.frame) {
          specStr = `${product.frame}`;
        } else if (product.Ram && product.Storage) {
          specStr = `${product.Ram} / ${product.Storage}`;
        } else if (product.Ram) {
          specStr = product.Ram;
        } else if (product.Storage) {
          specStr = product.Storage;
        } else if (product.Quantity) {
          specStr = `Qty: ${product.Quantity}`;
        }

        const identifier = product.barcode || product.OrderNumber;

        return `
          <div class="label" style="page-break-after: ${i < products.length - 1 ? "always" : "avoid"}">
            <div class="model">${title}</div>
            <div class="spec">${specStr}</div>
            <img class="barcode" src="${apiUrl}/barcodes/${encodeURIComponent(identifier)}.png"/>
            <div class="code">${identifier}</div>
          </div>`;
      })
      .join("");

    win.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <style>
          @page { size: 40mm 30mm; margin: 0; }
          html, body { width: 40mm; margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; }
          .label { width: 40mm; height: 30mm; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; overflow: hidden; padding: 1mm; box-sizing: border-box; }
          .model { font-size: 10pt; font-weight: bold; margin-bottom: 1mm; white-space: nowrap; overflow: hidden; width: 100%; }
          .spec { font-size: 8pt; margin-bottom: 1mm; color: #333; }
          .barcode { width: 34mm; height: 12mm; object-fit: contain; }
          .code { font-size: 7pt; margin-top: 1mm; font-family: 'Courier New', Courier, monospace; }
        </style>
      </head>
      <body>
        ${labelsHtml}
        <script>
          window.onload = () => {
            const imgs = document.querySelectorAll('img');
            let loaded = 0;
            const triggerPrint = () => { setTimeout(() => { window.print(); window.close(); }, 500); };
            if (imgs.length === 0) return triggerPrint();
            imgs.forEach(img => {
              img.onload = img.onerror = () => { loaded++; if (loaded === imgs.length) triggerPrint(); };
              if (img.complete) img.onload();
            });
          };
        <\/script>
      </body>
      </html>
    `);
    win.document.close();
  };

  const handleBulkPrint = async () => {
    const from = parseOrder(fromOrder);
    const to = parseOrder(toOrder);
    if (!from || !to) return toast.error("Enter valid orders (e.g. Mar26 001)");
    if (from.prefix !== to.prefix) return toast.error("Prefixes must match.");
    if (from.num > to.num) return toast.error("'From' must be <= 'To'");

    setPrintLoading(true);
    const fetched = [];
    for (let n = from.num; n <= to.num; n++) {
      const orderNum = buildOrderNumber(from.prefix, n, from.padLen);
      try {
        const res = await axios.get(`${apiUrl}/productByOrderNumber/${encodeURIComponent(orderNum)}`, {
          headers: { Authorization: `Bearer ${userId}` },
        });
        if (res.data) fetched.push(res.data);
      } catch (e) { }
    }
    setPrintLoading(false);

    if (fetched.length === 0) return toast.error("No products found in range.");
    printAllLabels(fetched);
  };

  const handleSinglePrint = () => {
    if (!product) return;
    printAllLabels([product]);
  };

  return (
    <div className="flex flex-col min-h-screen bg-slate-50 font-sans">
      <Header />
      <Sidebar />
      <div className="flex-1 flex flex-col items-center py-10 px-4 md:ml-64 mt-16 transition-all duration-300">

        <div className="bg-white p-6 rounded-2xl shadow-xl shadow-slate-200/50 w-full max-w-2xl border border-slate-100">
          <h1 className="text-3xl font-extrabold text-slate-800 mb-6 flex items-center gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm14 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg>
            Barcode Scanner
          </h1>

          {/* ═══════ Search Category Selection (Universal for Admin) ═══════ */}
          {userRole === "Admin" && (
            <div className="mb-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
                  <label className="text-sm font-bold text-slate-700 whitespace-nowrap uppercase tracking-wider">Search Category:</label>
                </div>
                <select
                  value={quickScanTable}
                  onChange={(e) => setQuickScanTable(e.target.value)}
                  className="flex-1 px-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold shadow-sm transition-all"
                >
                  <option value="all">🔍 All Categories (Auto-Detect)</option>
                  <option value="products">📱 Phones (products)</option>
                  <option value="otherproducts">📦 Others (otherproducts)</option>
                  <option value="screen">🖥️ Screens (screen)</option>
                  <option value="ethioProducts">🇪🇹 Ethio Products (ethioProducts)</option>
                  <option value="ethioOthers">➕ Ethio Others (ethioOthers)</option>
                </select>
              </div>
              <p className="text-[10px] text-slate-400 mt-2 ml-7 italic">
                {quickScanTable === "all"
                  ? "Looking everywhere... scanning will detect the category automatically."
                  : `Currently searching only in the ${quickScanTable} category.`}
              </p>
            </div>
          )}

          {/* ═══════ Quick Scan Mode Toggle ═══════ */}
          <div className={`mb-6 p-4 rounded-xl border-2 transition-all ${quickScanMode ? "bg-emerald-50 border-emerald-300" : "bg-slate-50 border-slate-200"}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{quickScanMode ? "⚡" : "🔄"}</span>
                <div>
                  <p className="font-bold text-slate-800">{quickScanMode ? "Quick Scan ON" : "Quick Scan Mode"}</p>
                  <p className="text-xs text-slate-500">Scan barcodes to auto-update status — no manual save needed</p>
                </div>
              </div>
              <button
                onClick={toggleQuickScanMode}
                className={`relative w-14 h-7 rounded-full transition-colors ${quickScanMode ? "bg-emerald-500" : "bg-slate-300"}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform ${quickScanMode ? "translate-x-7" : ""}`} />
              </button>
            </div>

            {/* Status selector — shown when quick scan is ON */}
            {quickScanMode && (
              <>
                <div className="mt-4 flex items-center gap-3">
                  <label className="text-sm font-semibold text-slate-600 whitespace-nowrap">Set status to:</label>
                  <div className="flex gap-2 flex-wrap">
                    {["Received"].map(status => (
                      <button
                        key={status}
                        onClick={() => setQuickScanStatus(status)}
                        className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${quickScanStatus === status
                          ? "bg-blue-500 text-white shadow-lg shadow-blue-200"
                          : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
                          }`}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-3">
                  <label className="text-sm font-semibold text-slate-600 whitespace-nowrap">Carrier:</label>
                  <input
                    type="number"
                    value={quickScanCarrier}
                    onChange={(e) => setQuickScanCarrier(e.target.value)}
                    placeholder="0.00"
                    className="px-3 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 font-medium w-32 shadow-sm"
                  />
                  <span className="text-xs text-slate-400 italic">(Auto-applied on scan)</span>
                </div>
              </>
            )}
          </div>

          {/* Scanner area — always in DOM, hidden when product is found (normal mode only) */}
          <div className={(!quickScanMode && product) ? "hidden" : "flex flex-col items-center"}>

            {/* Camera Switcher */}
            {!scanning && (
              <div className="w-full max-w-md mb-4">
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Switch Camera Lens</label>
                <select
                  value={selectedCamera}
                  onChange={(e) => setSelectedCamera(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium"
                >
                  {cameras.map((cam, idx) => (
                    <option key={cam.id} value={cam.id}>
                      {cam.label || `Camera ${idx + 1}`}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-orange-500 mt-1 mt-2">
                  Tip: If the barcode stays blurry, switch cameras here or hold the phone farther away.
                </p>
              </div>
            )}

            <div className="w-full flex justify-center">
              <div
                id="reader"
                className="w-full max-w-md rounded-xl overflow-hidden shadow-inner"
                style={{
                  minHeight: "280px",
                  background: "#000",
                  position: "relative",
                  border: scanning
                    ? quickScanMode ? "2px solid #10b981" : "2px solid #6366f1"
                    : "2px dashed #c7c9e5"
                }}
              />
            </div>

            {/* Quick scan active indicator */}
            {quickScanMode && scanning && (
              <div className="mt-3 flex items-center gap-2 text-emerald-600 font-semibold animate-pulse">
                <span className="w-3 h-3 bg-emerald-500 rounded-full"></span>
                Quick scanning — auto-setting {quickScanTable === "all" ? "detected item" : `[${quickScanTable}]`} to "{quickScanStatus}"
              </div>
            )}

            {/* Camera controls */}
            <div className="flex gap-3 mt-4">
              {!scanning ? (
                <button
                  onClick={startScanning}
                  className={`px-5 py-2.5 text-white rounded-lg font-semibold shadow-lg transition-all active:scale-95 flex items-center gap-2 ${quickScanMode
                    ? "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200"
                    : "bg-indigo-600 hover:bg-indigo-700 shadow-indigo-200"
                    }`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                  Start Camera
                </button>
              ) : (
                <button
                  onClick={stopScanning}
                  className="px-5 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold shadow-lg shadow-red-200 transition-all active:scale-95 flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" /></svg>
                  Stop Camera
                </button>
              )}

              {/* File upload button */}
              <label className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold transition-all cursor-pointer flex items-center gap-2 border border-slate-200">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                Upload Image
                <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
              </label>
            </div>

            {/* Camera error message */}
            {cameraError && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm w-full max-w-md">
                <p className="font-semibold mb-1">⚠️ Camera Issue</p>
                <p className="whitespace-pre-line">{cameraError}</p>
              </div>
            )}

            {/* Manual input */}
            <div className="mt-8 w-full">
              <div className="flex items-center text-sm text-slate-400 mb-4 px-4 before:flex-1 before:border-t before:border-slate-200 before:mr-4 after:flex-1 after:border-t after:border-slate-200 after:ml-4">OR TYPE ORDER NO.</div>
              <form onSubmit={handleManualSearch} className="flex gap-2">
                <input
                  type="text"
                  value={scannedCode}
                  onChange={(e) => setScannedCode(e.target.value)}
                  placeholder="Type Order No (e.g. Mar26004)"
                  className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow"
                />
                <button type="submit" disabled={loading} className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold shadow-lg shadow-indigo-200 transition-all active:scale-95 disabled:opacity-70">
                  {loading ? "Searching..." : quickScanMode ? "Update" : "Search"}
                </button>
              </form>
            </div>

            {/* ═══════ Quick Scan Log ═══════ */}
            {quickScanMode && quickScanLog.length > 0 && (
              <div className="mt-6 w-full">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wider">Scan History ({quickScanLog.length})</h3>
                  <button
                    onClick={() => setQuickScanLog([])}
                    className="text-xs text-red-500 hover:text-red-700 font-semibold"
                  >
                    Clear
                  </button>
                </div>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {quickScanLog.map((item, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center justify-between p-3 rounded-lg text-sm ${item.status === "FAILED"
                        ? "bg-red-50 border border-red-200"
                        : "bg-emerald-50 border border-emerald-200"
                        }`}
                    >
                      <div>
                        <span className="font-bold text-slate-800">{item.order}</span>
                        <span className="text-slate-500 ml-2">{item.brand} {item.model}</span>
                        {item.table && item.table !== "products" && (
                          <span className="ml-2 px-1.5 py-0.5 bg-slate-200 text-[10px] rounded text-slate-600 uppercase">{item.table}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right mr-2">
                          <div className={`px-2 py-0.5 rounded-full text-xs font-bold ${item.status === "Sold" ? "bg-emerald-200 text-emerald-800"
                            : item.status === "Unpaid" ? "bg-amber-200 text-amber-800"
                              : item.status === "Received" ? "bg-blue-200 text-blue-800"
                                : "bg-red-200 text-red-800"
                            }`}>{item.status}</div>
                          {item.carrier !== "—" && (
                            <div className="text-[10px] font-bold text-slate-500 mt-0.5">ETB {item.carrier}</div>
                          )}
                        </div>
                        <span className="text-xs text-slate-400">{item.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Product details — shown when product is found (normal mode only) */}
          {!quickScanMode && product && (
            <div className="space-y-6 animate-fade-in">
              <div className="bg-indigo-50/50 p-6 rounded-xl border border-indigo-100 flex gap-6 items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-indigo-500 uppercase tracking-widest mb-1">Product Found</p>
                  <h2 className="text-2xl font-bold text-slate-800">{product.Brand} {product.Model}</h2>
                  <p className="text-slate-500">{product.Storage || product.frame} | {product.Ram || ""} | {product.Sim || ""}</p>
                  <p className="inline-block mt-3 px-3 py-1 bg-white border border-slate-200 text-xs font-mono rounded-lg shadow-sm text-slate-600">Order: {product.OrderNumber}</p>
                </div>
                <div className="flex-1 flex flex-col items-end gap-3">
                  {product.ImageFilename1 || product.ImageFilename2 ? (
                    <div className="flex flex-row gap-2 h-32">
                      {[`${apiUrl}/uploads/${product.ImageFilename1}`, `${apiUrl}/uploads/${product.ImageFilename2}`]
                        .filter(Boolean)
                        .map((img, index) => (
                          <img
                            key={index}
                            src={img}
                            alt=""
                            height="20"
                            width="100"
                            className="cursor-pointer rounded-lg object-cover shadow-sm"
                          />
                        ))}
                    </div>
                  ) : (
                    <img
                      src={noImage}
                      alt="No Image Available"
                      height="10"
                      width="50"
                      className="cursor-pointer"
                    />
                  )}
                  <button
                    onClick={handleSinglePrint}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded-lg font-bold text-sm transition-colors border border-indigo-100 shadow-sm"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                    Print Label
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                {/*  Status Editor */}
                <div className="p-4 rounded-xl border border-slate-100 bg-white shadow-sm hover:shadow-md transition-shadow">
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Store Stat</label>
                  <select name="ourStat" value={editData.ourStat} onChange={handleChange} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium">
                    <option value="Sold">Sold</option>
                    <option value="Unpaid">Unpaid</option>
                    <option value="Received">Received</option>
                  </select>
                </div>

                {/* Seller Editor */}
                <div className="p-4 rounded-xl border border-slate-100 bg-white shadow-sm hover:shadow-md transition-shadow">
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Seller</label>
                  <input type="text" name="seller" value={editData.seller || userName} onChange={handleChange} placeholder={userName} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium" />
                </div>

                {/* Buyer Editor */}
                <div className="p-4 rounded-xl border border-slate-100 bg-white shadow-sm hover:shadow-md transition-shadow">
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Buyer</label>
                  <input type="text" name="buyer" value={editData.buyer} onChange={handleChange} placeholder="Buyer Name" className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium" />
                </div>

                {/* Price Editor */}
                <div className="p-4 rounded-xl border border-slate-100 bg-white shadow-sm hover:shadow-md transition-shadow">
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Price (ETB)</label>
                  <input type="number" name="soldPrice" value={editData.soldPrice} onChange={handleChange} placeholder="soldPrice" className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium" />
                </div>

                {/* sales Date Editor */}
                <div className="p-4 rounded-xl border border-slate-100 bg-white shadow-sm hover:shadow-md transition-shadow">
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Sales Date</label>
                  <input type="date" name="salesDate" value={editData.salesDate} onChange={handleChange} placeholder="Sales Date" className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium" />
                </div>
                <div className="p-4 rounded-xl border border-slate-100 bg-white shadow-sm hover:shadow-md transition-shadow">
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Carrier (ETB)</label>
                  <input type="number" name="carrier" value={editData.carrier} onChange={handleChange} placeholder="Carrier" className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium" />
                </div>
              </div>

              <div className="pt-6 border-t border-slate-100 flex justify-between">
                <button onClick={resumeScan} className="px-6 py-3 bg-red-100 hover:bg-red-200 text-red-700 rounded-xl font-semibold transition-colors flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" /></svg>
                  Scan Another
                </button>
                <button
                  onClick={handleSaveAll}
                  disabled={loading}
                  className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold shadow-lg shadow-indigo-200 transition-all active:scale-95 disabled:opacity-70 flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  {loading ? "Saving..." : "Save All"}
                </button>
              </div>
            </div>
          )}

          {/* ═══════ Bulk Print Labels Section ═══════ */}
          <div className="mt-8 p-6 bg-slate-100/50 rounded-2xl border border-slate-200 border-dashed">
            <h3 className="text-lg font-bold text-slate-700 mb-4 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
              Bulk Print Labels
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">From Order No.</label>
                <input
                  type="text"
                  value={fromOrder}
                  onChange={(e) => setFromOrder(e.target.value)}
                  placeholder="e.g. Mar26 001"
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 font-medium shadow-sm"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">To Order No.</label>
                <input
                  type="text"
                  value={toOrder}
                  onChange={(e) => setToOrder(e.target.value)}
                  placeholder="e.g. Mar26 010"
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 font-medium shadow-sm"
                />
              </div>
              <div className="sm:col-span-2 mt-2">
                <button
                  onClick={handleBulkPrint}
                  disabled={printLoading}
                  className="w-full py-3 bg-slate-700 hover:bg-slate-800 text-white rounded-xl font-bold shadow-lg shadow-slate-200 transition-all active:scale-[0.98] disabled:opacity-70 flex items-center justify-center gap-2"
                >
                  {printLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Fetching...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                      Print Label Range
                    </>
                  )}
                </button>
                <p className="text-[10px] text-slate-400 mt-3 text-center italic">
                  Note: This will search across all categories (Phones, Others, Screens, Ethio).
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ScannerPage;
